import isempty from 'lodash/isEmpty';
import size from 'lodash/size';
import {
    isEqual,
    formatDate,
    subtractDaysFromDate,
    addDaysToDate,
    isDateObject,
    isDateString,
    isBefore,
    isPast
} from './dateHelper';
import {UnitAvailability} from './UnitAvailability';
import {convertDashesToSlashes} from './dateHelper';
import differenceInDays from 'date-fns/difference_in_days';

const DATE_FORMAT = 'YYYY-MM-DD';

function getAvailabilityObject(availabilityCalendar) {
    // Data sometimes comes back in 'availability' object and sometimes in 'availabilityData' object
    // We need to choose the one that has a valid unitAvailabilityConfiguration property
    return availabilityCalendar && !isempty(availabilityCalendar)
        ? availabilityCalendar.availability || availabilityCalendar.availabilityData
        : {};
}

function getNextDay(date) {
    if (date) {
        date.setDate(date.getDate() + 1);

        return {
            localDate: date,
            formatted: formatDate(date, DATE_FORMAT, true)
        };
    }

    return {
        localDate: null,
        formatted: null
    };
}

function isMinStayMet(tripLength, minStay) {
    return tripLength >= minStay;
}

function isStayingTooLong(tripLength, maxStay) {
    return maxStay > 0 && tripLength > maxStay;
}

function isBlockedByABookedDay(blockedByABookedDay, previousDayBooked) {
    return blockedByABookedDay ? true : previousDayBooked;
}

function isBeforeArrival(arrival, daysSinceArrival) {
    return arrival && arrival.formatted && daysSinceArrival < 0;
}
function isUnfocused(arrival, daysSinceArrival, blockedByABookedDay) {
    const beforeArrival = isBeforeArrival(arrival, daysSinceArrival);
    return beforeArrival || blockedByABookedDay;
}

function getDatelessAvailabilityForDay(thisDay) {
    return {
        disabled:
            thisDay.inThePast ||
            (!thisDay.available && (!thisDay.canCheckIn || !thisDay.canCheckOut)) ||
            !thisDay.minPriorNotifyMet,
        booked: !thisDay.available,
        previousDayBooked: thisDay.previousDayBooked,
        today: thisDay.isToday,
        inPast: thisDay.inThePast
    };
}

function getCheckInAvailabilityForDay(thisDay) {
    return {
        disabled: thisDay.inThePast || !thisDay.canCheckIn || !thisDay.minPriorNotifyMet,
        booked: !thisDay.available,
        previousDayBooked: thisDay.previousDayBooked,
        today: thisDay.isToday,
        inPast: thisDay.inThePast,
        isCheckInDay: thisDay.isCheckInDay,
        minPriorNotifyMet: thisDay.minPriorNotifyMet,
        minStayCanBeMet: thisDay.minStayCanBeMet,
        minStay: thisDay.minStay
    };
}

function getCheckOutAvailabilityForDay(thisDay) {
    return {
        disabled:
            !thisDay.minStayMet ||
            thisDay.stayingTooLong ||
            thisDay.inThePast ||
            !thisDay.canCheckOut ||
            thisDay.blockedByABookedDay,
        booked: !thisDay.available,
        previousDayBooked: thisDay.previousDayBooked,
        today: thisDay.isToday,
        inPast: thisDay.inThePast,
        isCheckOutDay: thisDay.canCheckOut,
        minStayMet: thisDay.minStayMet,
        stayingTooLong: thisDay.stayingTooLong,
        unfocused: thisDay.unfocused,
        isBeforeArrival: thisDay.isBeforeArrival
    };
}

function getDatelessTransformedDefaults(ua) {
    const index = -1;

    return {
        booked: false,
        disabled: !ua.isAvailable(index) && !ua.isCheckOutDay(index),
        inPast: false,
        previousDayBooked: false,
        today: false
    };
}

function getCheckInTransformedDefaults(ua) {
    const index = -1;

    return {
        booked: false,
        disabled: !ua.isAvailable(index) || !ua.isCheckInAllowed(index),
        inPast: false,
        previousDayBooked: false,
        today: false,
        minStay: ua.getMinStayValue(index)
    };
}

function getCheckOutTransformedDefaults(ua) {
    return {
        booked: false,
        disabled: !ua.isAvailable(-1) || !ua.isCheckOutDay(-1),
        inPast: false,
        minStayMet: false,
        previousDayBooked: false,
        stayingTooLong: false,
        today: false
    };
}

function getTransformedDefaults(ua, type) {
    switch (type) {
        case 'checkin':
            return getCheckInTransformedDefaults(ua);
        case 'checkout':
            return getCheckOutTransformedDefaults(ua);
        default:
            return getDatelessTransformedDefaults(ua);
    }
}

// when choosing an end date and the default availability is "available", we need
// to switch the default to "unavailable" so the out of range dates don't show as selectable
// when they are blocked by a booked day
function handleDefaultAvailability(blockedByABookedDay, hasDepartureDate, transformedAvailability) {
    if (blockedByABookedDay && !hasDepartureDate) {
        transformedAvailability.checkout.defaults.disabled = true;
        transformedAvailability.checkout.defaults.booked = false;
        transformedAvailability.checkout.defaults.previousDayBooked = false;
        transformedAvailability.checkout.defaults.unfocused = true;
    }
}

// loop through default dates before first date of availability range (if any) and set them all
// to unfocused when a start date is selected
function handleEarlyDefaults(arrival, firstDate, startDateLocal, transformedAvailability) {
    while (arrival && arrival.localDate && +firstDate.localDate <= +startDateLocal) {
        transformedAvailability.checkout[firstDate.formatted] = {unfocused: true};
        firstDate = getNextDay(firstDate.localDate);
    }
}

function incrementDateIndex(currentDate, beginDate, index) {
    return +currentDate >= +beginDate ? ++index : index;
}

/* eslint-disable complexity */
function transformAvailability({
    ua,
    today,
    startDateLocal,
    stopDateLocal,
    arrival,
    hasDepartureDate,
    opts = {dateless: true, checkin: true, checkout: true}
}) {
    const transformedAvailability = {};
    const validOptKeys = ['dateless', 'checkin', 'checkout'];
    const trueOpts = Object.keys(opts).filter(
        (opt) => validOptKeys.indexOf(opt) > -1 && !!opts[opt]
    );

    trueOpts.forEach((opt) => {
        transformedAvailability[opt] = {};
    });

    if (!ua) {
        return transformedAvailability;
    }

    // get default values for any/all of dateless, checkin, and checkout
    trueOpts.forEach((opt) => {
        transformedAvailability[opt].defaults = getTransformedDefaults(ua, opt);
    });

    let minStayForArrivalDate = 0;
    let maxStayForArrivalDate = 0;
    let dateIndex = -1;
    let daysSinceArrival = -1;
    let previousDayBooked = false;
    let blockedByABookedDay = false;

    let thisDay = {
        blockedByABookedDay: false,
        localDate: startDateLocal,
        formatted: formatDate(startDateLocal, DATE_FORMAT, true)
    };

    const beginDate = ua.getBeginDate();
    const beginDateLocal = beginDate.local;
    const beginDateOffsetFromToday = differenceInDays(today.formatted, beginDate.text) || 0;

    let firstDate = Object.assign({}, today);
    firstDate.localDate = new Date(today.localDate);

    if (transformedAvailability.checkout) {
        handleEarlyDefaults(arrival, firstDate, startDateLocal, transformedAvailability);
    }

    while (+thisDay.localDate <= +stopDateLocal) {
        dateIndex = incrementDateIndex(thisDay.localDate, beginDateLocal, dateIndex);
        thisDay.inThePast = isBefore(thisDay.localDate, today.localDate);
        thisDay.previousDayBooked = previousDayBooked;
        thisDay.isToday = isEqual(today.formatted, thisDay.formatted);
        thisDay.minStay = ua.getMinStay({index: dateIndex});

        if (arrival && isEqual(arrival.formatted, thisDay.formatted)) {
            // is arrival date. trip is starting.
            daysSinceArrival = 0;
            minStayForArrivalDate = thisDay.minStayForArrivalDate = thisDay.minStay;
            maxStayForArrivalDate = thisDay.maxStayForArrivalDate = ua.getMaxStay(dateIndex);
        } else if (daysSinceArrival >= 0) {
            // has trip started
            daysSinceArrival++;
            blockedByABookedDay = isBlockedByABookedDay(blockedByABookedDay, previousDayBooked);

            if (transformedAvailability.checkout) {
                handleDefaultAvailability(
                    blockedByABookedDay,
                    hasDepartureDate,
                    transformedAvailability
                );
            }
        }

        thisDay.canCheckIn = ua.isCheckInAllowed(dateIndex);
        // Consider the day available if a) The indexed data says it is availab, and
        // b) we can check in along with the previous day not being booked.
        thisDay.available =
            ua.isAvailable(dateIndex) && !(!thisDay.canCheckIn && thisDay.previousDayBooked);
        thisDay.canCheckOut = ua.isCheckOutDay(dateIndex);
        thisDay.minStayMet = isMinStayMet(daysSinceArrival, minStayForArrivalDate);
        thisDay.stayingTooLong = isStayingTooLong(daysSinceArrival, maxStayForArrivalDate);
        thisDay.minPriorNotifyMet = ua.minPriorNotifyMet(dateIndex, beginDateOffsetFromToday);
        thisDay.isCheckInDay = ua.isCheckInDay(dateIndex);
        thisDay.minStayCanBeMet = ua.canMinStayBeMet({index: dateIndex, minValue: 1});
        thisDay.isBeforeArrival = isBeforeArrival(arrival, daysSinceArrival);
        thisDay.blockedByABookedDay = blockedByABookedDay;
        thisDay.unfocused = isUnfocused(arrival, daysSinceArrival, blockedByABookedDay);

        if (transformedAvailability.dateless) {
            transformedAvailability.dateless[thisDay.formatted] = getDatelessAvailabilityForDay(
                thisDay
            );
        }

        if (transformedAvailability.checkin) {
            transformedAvailability.checkin[thisDay.formatted] = getCheckInAvailabilityForDay(
                thisDay
            );
        }

        if (transformedAvailability.checkout) {
            transformedAvailability.checkout[thisDay.formatted] = getCheckOutAvailabilityForDay(
                thisDay
            );
        }

        previousDayBooked = !thisDay.available;

        thisDay = getNextDay(thisDay.localDate);
    }

    return transformedAvailability;
}
/* eslint-enable complexity */

function getStartDate(beginDateLocal, arrival, isAvailableByDefault) {
    let startDateLocal = beginDateLocal;

    if (arrival.localDate && +arrival.localDate < +beginDateLocal && isAvailableByDefault) {
        startDateLocal = arrival.localDate;
    }

    return startDateLocal;
}

function getStopDate(ua, endDateLocal, arrival, isAvailableByDefault) {
    let stopDateLocal = endDateLocal;

    if (
        arrival &&
        arrival.localDate &&
        +arrival.localDate > +endDateLocal &&
        isAvailableByDefault
    ) {
        const defaults = ua.getDefaults();
        const maxStayDefault = defaults.maxStay;
        let daysAhead = maxStayDefault > 0 ? maxStayDefault : defaults.minStay;
        stopDateLocal = addDaysToDate(arrival.localDate, daysAhead);
    }

    return stopDateLocal;
}

function getDateObject(date) {
    const dateIsString = isDateString(date);
    const dateIsObject = isDateObject(date);

    if (dateIsString || dateIsObject) {
        const dateEl = dateIsString ? new Date(convertDashesToSlashes(date)) : date;
        return {
            localDate: dateEl,
            formatted: formatDate(dateEl, DATE_FORMAT, true)
        };
    }

    return {
        localDate: null,
        formatted: null
    };
}

/*
    calculateAvailability prepares a set of data for styling the availability calendar.

    It produces a map of three objects based on dateless, checkin and checkout.
    These three sets should be used in different situations:
    a) Dateless - The basic availability calendar normally seen on the main PDP page.
    b) checkin - For a datepicker, when you are choosing a check in date.
    c) checkout - After an arrival date is chosen, a different set of available dates are presented.

    Each map is indexed by day; each day containing information about
    that day's availability.

    The date range covered is based on the availabilityCalendar's date range.
    {
        ...
        ...
        2017-06-11: {
            disabled: (Bool) The date should be considered disabled.
            booked: (Bool) The date is already be filled by another visit.
            previousDayBooked: (Bool) The previous day was booked.  This is a shortcut
                            data attribute to help determine how the day should be filled
                            without having to compare other day data.
            today: (Bool) This date should be considered the current day.
            inPast: (Bool) This date is in the past.  It is another shortcut data item
                        so you don't have to keep track.
        },
        2017-06-12: {
            etc. etc..
        },
        ...
    }

    Common use of this might be:
        Full Gray Square - booked and previousDayBooked
        TopLeft only gray, disabled - previousDayBooked and !booked && !availableForCheckin
        TopLeft only gray, enabled - previousDayBooked and !booked && availableForCheckin
        BottomRight only gray - !previousDayBooked and booked
        White, but disabled - !booked & !previousDayBooked && !availableForCheckin
        White, and enabled - !booked & !previousDayBooked && availableForCheckin

    Calculations and data based on this spec:
    https://wiki.homeawaycorp.com/display/~mkinman/2014/02/09/Unit+Availability
*/
function calculateAvailability(availabilityCalendar, todayDate, arrivalDate, departureDate, opts) {
    let emptyAvailability = {dateless: {}, checkin: {}, checkout: {}};
    const availabilityObject = getAvailabilityObject(availabilityCalendar);

    if (isempty(availabilityObject)) {
        // missing availability data
        return emptyAvailability;
    }

    const ua = new UnitAvailability(availabilityObject);
    const today = getDateObject(todayDate);
    const arrival = getDateObject(arrivalDate);
    const isAvailableByDefault = ua.isAvailable(-1);
    const beginDateLocal = ua.getBeginDate().local;
    const startDateLocal = getStartDate(beginDateLocal, arrival, isAvailableByDefault);
    const stopDateLocal = ua.getEndDate().local;

    return transformAvailability({
        ua,
        hasDepartureDate: !!departureDate,
        today,
        startDateLocal,
        stopDateLocal,
        arrival,
        opts
    });
}

function isObjectWithData(possibleObject) {
    return !!possibleObject && typeof possibleObject === 'object' && !isempty(possibleObject);
}

function dateIsMatch(dateObj, matchCriteria) {
    /*
     * iterate each key/value pair in match criteria
     * for each, check if dateObj[key] is value
     * as long as the values match, return true
     * if we find a mismatch, return false
     */
    if (!isObjectWithData(dateObj) || !isObjectWithData(matchCriteria)) {
        return null;
    }

    for (let key of Object.keys(matchCriteria)) {
        if (dateObj[key] !== matchCriteria[key]) {
            return false;
        }
    }

    return true;
}

function canHaveClosestDate(date, transformedAvailability, matchCriteria) {
    return !!date && isObjectWithData(transformedAvailability) && isObjectWithData(matchCriteria);
}

function prepDateForComparison(transformedAvailability, date, matchCriteria) {
    let availabilityForDate = {};
    availabilityForDate.formatted = formatDate(date);
    availabilityForDate.availability =
        transformedAvailability[availabilityForDate.formatted] || null;

    if (!availabilityForDate.availability) {
        if (transformedAvailability.defaults) {
            availabilityForDate.availability = transformedAvailability.defaults;
            availabilityForDate.availability.inPast = isPast(date);
            availabilityForDate.isMatch = dateIsMatch(
                availabilityForDate.availability,
                matchCriteria
            );

            return availabilityForDate;
        }

        return null;
    }

    availabilityForDate.isMatch = dateIsMatch(availabilityForDate.availability, matchCriteria);

    return availabilityForDate;
}

function chooseClosestDate(futureDate, pastDate, leanEarly) {
    if ((futureDate.isMatch && !pastDate.isMatch) || (futureDate.isMatch && !leanEarly)) {
        return futureDate.formatted;
    }

    if ((pastDate.isMatch && !futureDate.isMatch) || (pastDate.isMatch && leanEarly)) {
        return pastDate.formatted;
    }

    return null;
}

/* eslint-disable complexity */
function getClosestDate(date, transformedAvailability, matchCriteria, leanEarly) {
    if (!canHaveClosestDate(date, transformedAvailability, matchCriteria)) {
        return null;
    }

    const dateRangeSize = size(transformedAvailability);
    const closestToDate = new Date(date);

    for (let i = 0; i < dateRangeSize; i++) {
        const addedDay = addDaysToDate(closestToDate, i);
        const subtractedDay = subtractDaysFromDate(closestToDate, i);

        const futureDate = prepDateForComparison(transformedAvailability, addedDay, matchCriteria);
        const pastDate = prepDateForComparison(
            transformedAvailability,
            subtractedDay,
            matchCriteria
        );

        if (!futureDate || !pastDate) {
            return null;
        }

        const closestDate = chooseClosestDate(futureDate, pastDate, leanEarly);

        if (closestDate) {
            return closestDate;
        }
    }

    /* istanbul ignore next */
    return null;
}
/* eslint-enable complexity */

/**
* calculateRatesAndAvailability
* Extends the functionality of calculateAvailability by also accepting rate data and returning an availability that includes rates
 * @param {Object} availabilityCalendar - collection of availability statuses for a range of dates, eg. {
         dateRange: {
             beginDate: '2016-12-21',
             endDate: '2019-12-20'
         },
         unitAvailabilityConfiguration: {
             availability: 'YYNNNNNNNNNNYYYYYYYYYYYYYYYNYYYYYYY...',
             changeOver: 'IXOXXXXXXXXXIICCCCCCCCCCCCCCCCCCCCCCC...',
             maxStay: '0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,000,0,0,...',
             minPriorNotify: '1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,...',
             minStay: '2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,22,2,2,2,2...',
             stayIncrement: 'DDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDDD...'
         }
    }
 * @param {Date} today
 * @param {String} arriveDate - Trip start date
 * @param {Object} rateData - collection of nightly rates for a range of dates between a start and end date,
    rentNights needs to be an array to support strings of numbers with commas in the formatting, eg. {
        beginDate: Thu Jun 20 2017 00:00:00 GMT-0500 (CDT)
        endDate: Wed Jun 19 2019 00:00:00 GMT-0500 (CDT)
        rentNights: ['$1,800','$180','$180','$200','$200','$180','$180','$180']
    }

    @returns
    {
        '2017-06-20': {
            availableForCheckin: true,
            availableForCheckout: true,
            booked: true,
            previousDayBooked: false,
            today: true,
            inPast: false,
            rate: '$1,800'
        },
        '2017-06-21': ...
    },
 */

/* eslint-disable complexity */
function processAvailabilityObject(
    rateAndAvailabilityObject,
    key,
    beginDate,
    endDate,
    rentNightsArr
) {
    // start with indexedDay as the ratesData begin date
    // loop through every day until the ratesData end date
    // if that date exists as a key on availabilityData, then add to that key "rate: ratesData[i]"
    // if that date doesn't exists as a key, then add a key with "date: {rate: ratesData[i]}"
    const endDayDateObj = endDate instanceof Date ? endDate : new Date(endDate);
    let currDayDateObj = beginDate instanceof Date ? beginDate : new Date(beginDate);
    let currObj = rateAndAvailabilityObject[key];
    let currDateFormatted;
    let currRate;
    let i = 0;

    while (currDayDateObj <= endDayDateObj) {
        currDateFormatted = formatDate(currDayDateObj);
        currRate = rentNightsArr[i] ? rentNightsArr[i].toString() : '';

        /* istanbul ignore else */
        if (currObj[currDateFormatted]) {
            currObj[currDateFormatted].rate = currRate;
        } else {
            currObj[currDateFormatted] = {
                rate: currRate
            };
        }
        i++;
        currDayDateObj.setUTCDate(currDayDateObj.getUTCDate() + 1);
    }
}
/* eslint-enable complexity */

/* eslint-disable complexity */
function calculateRatesAndAvailability(availabilityCalendar, today, arriveDate, rateData, opts) {
    const rateAndAvailabilityObject = calculateAvailability(
        availabilityCalendar,
        today,
        arriveDate,
        null,
        opts
    );
    let rentNightsArr;

    if (rateData && rateData.rentNights) {
        rentNightsArr = rateData.rentNights;
    } else {
        return rateAndAvailabilityObject;
    }

    const beginDate = rateData.beginDate;
    const endDate = rateData.endDate;

    for (const key in rateAndAvailabilityObject) {
        // hasOwnProperty is difficult to test coverage on.
        /* istanbul ignore next */
        if (rateAndAvailabilityObject.hasOwnProperty(key)) {
            processAvailabilityObject(
                rateAndAvailabilityObject,
                key,
                beginDate,
                endDate,
                rentNightsArr
            );
        }
    }
    return rateAndAvailabilityObject;
}
/* eslint-enable complexity */

export default calculateAvailability;
export {
    getAvailabilityObject,
    calculateAvailability,
    calculateRatesAndAvailability,
    dateIsMatch,
    getClosestDate,
    getNextDay,
    isMinStayMet,
    isStayingTooLong,
    isBlockedByABookedDay,
    isUnfocused,
    getDatelessAvailabilityForDay,
    getCheckInAvailabilityForDay,
    getCheckOutAvailabilityForDay,
    getDatelessTransformedDefaults,
    getCheckInTransformedDefaults,
    getCheckOutTransformedDefaults,
    handleDefaultAvailability,
    handleEarlyDefaults,
    transformAvailability,
    getStartDate,
    getStopDate,
    getDateObject,
    incrementDateIndex,
    isObjectWithData,
    canHaveClosestDate,
    prepDateForComparison,
    chooseClosestDate
};
